// Copyright (c) StrangeBee 2024

import React, { ReactElement, useState } from 'react';
import { IntlProvider, MessageFormatElement } from 'react-intl';
import flatten from 'flat';
import {
    AvailableLanguages,
    AvailableLanguagesArray,
    LanguageDirection,
    LanguageSwitcherContext,
    StorageLanguageType,
} from './LanguageSwitcherContext';
import useLocalStorage, { LocalStorageKeys } from '@Hooks/useLocalStorage';
import ar from './ar.json';
import de from './de.json';
import en from './en.json';
import en_US from './en_US.json';
import es from './es.json';
import fr from './fr.json';
import it from './it.json';
import nl from './nl.json';
import ja from './ja.json';
import pl from './pl.json';
import pt_BR from './pt_BR.json';
import ru from './ru.json';
import sv from './sv.json';
import zh_Hans from './zh_Hans.json';

const isAvailableLanguage = (language: string | AvailableLanguages): language is AvailableLanguages => {
    return AvailableLanguagesArray.includes(language as any);
};

const messages: Record<AvailableLanguages, any> = {
    ar,
    de,
    en,
    en_US,
    es,
    fr,
    it,
    ja,
    nl,
    pl,
    pt_BR,
    ru,
    sv,
    zh_Hans,
};

interface CustomIntlProviderProps {
    children: React.ReactNode;
}

const defaultFlattenMessage = flatten(messages.en) as Record<string, string> | Record<string, MessageFormatElement[]>;

const CustomIntlProvider = ({ children }: CustomIntlProviderProps): ReactElement => {
    const { storage } = useLocalStorage<StorageLanguageType>({
        key: LocalStorageKeys.LANGUAGE_SWITCHER,
        initialValue: { language: 'en', direction: 'ltr' },
    });
    const navigatorLanguage = navigator.language.split(/[-_]/)[0];
    const [language, setLanguage] = useState<AvailableLanguages>(
        storage.language || (isAvailableLanguage(navigatorLanguage) && navigatorLanguage),
    );
    const [direction, setDirection] = useState<LanguageDirection>(storage.direction || 'ltr');
    const defaultMessages = messages[language];

    const flattenMessage = flatten(defaultMessages) as Record<string, string> | Record<string, MessageFormatElement[]>;

    return (
        <LanguageSwitcherContext.Provider value={{ language, setLanguage, direction, setDirection }}>
            <IntlProvider
                defaultLocale="en"
                locale={language.replace('_', '-')}
                messages={
                    { ...defaultFlattenMessage, ...flattenMessage } as
                        | Record<string, string>
                        | Record<string, MessageFormatElement[]>
                }
            >
                {children}
            </IntlProvider>
        </LanguageSwitcherContext.Provider>
    );
};

export default CustomIntlProvider;
